// hooks/market/useMyShopProducts.ts
//
// Récupère les produits d'UNE boutique dont l'utilisateur est owner,
// TOUS statuts (draft/published/disabled) — pour la section Produits
// de app/(tabs)/market/gerer/[shopId].tsx. Distinct de
// useShopProducts.ts (public, published only, utilisé par
// boutiques/[slug].tsx).
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'get-my-shop-products', { shop_id }
// (voir backend/handleMarket.ts → getMyShopProducts)

import { useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

export type MyShopProduct = {
  id: string;
  name: string;
  slug: string;
  cover_image_url: string | null;
  price: number;
  promo_price: number | null;
  currency_code: string;
  stock_quantity: number;
  status: 'draft' | 'published' | 'disabled' | 'archived';
  is_featured: boolean;
  category_id: string;
  created_at: string;
};

const PAGE_SIZE = 20;

export function useMyShopProducts(shopId: string | undefined) {
  const [products, setProducts] = useState<MyShopProduct[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef  = useRef(0);
  const loadingRef = useRef(false);

  const fetchProducts = useCallback(async (mode: 'initial' | 'more') => {
    if (!shopId || loadingRef.current) return;
    loadingRef.current = true;

    if (mode === 'initial') setLoading(true);
    if (mode === 'more')    setLoadingMore(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const currentOffset = mode === 'more' ? offsetRef.current : 0;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-my-shop-products',
          access_token: token,
          shop_id:      shopId,
          limit:        PAGE_SIZE,
          offset:       currentOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger les produits');
        return;
      }

      const newProducts: MyShopProduct[] = data.products || [];

      if (mode === 'more') {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          return [...prev, ...newProducts.filter(p => !existingIds.has(p.id))];
        });
      } else {
        setProducts(newProducts);
      }

      offsetRef.current = data.next_offset ?? (currentOffset + newProducts.length);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      console.error('[useMyShopProducts]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [shopId]);

  useEffect(() => {
    offsetRef.current = 0;
    fetchProducts('initial');
  }, [fetchProducts]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchProducts('more');
  }, [hasMore, fetchProducts]);

  const refresh = useCallback(() => {
    offsetRef.current = 0;
    fetchProducts('initial');
  }, [fetchProducts]);

  return { products, loading, loadingMore, error, hasMore, loadMore, refresh };
}
  
